import { Component, inject, OnInit, TemplateRef, ViewChild } from '@angular/core'
import { map, Observable, Subject, Subscription, take, takeUntil } from 'rxjs'
import { CommonModule, NgIf } from '@angular/common'
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms'
import { RouterModule } from '@angular/router'
import { CapitalizePipe } from '../../../pipes/capitalize.pipe'
import { BlockUnblockComponent } from '../template/block-unblock/block-unblock.component'
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap'
// import { trigger, transition, style, animate } from '@angular/animations'
import { DeleteComponent } from '../template/delete/delete.component'
import { select, Store } from '@ngrx/store'
import { PaginationComponent } from '../template/pagination/pagination.component'
import { Business } from '../../../store/business/business.state'
import { selectAllBusinesses } from '../../../store/business/business.selectors'
import { loadBusinesses } from '../../../store/business/business.actions'
import {
  blockDepartment,
  blockDepartmentFailure,
  blockDepartmentSuccess,
  createDepartment,
  createDepartmentSuccess,
  deleteDepartment,
  deleteDepartmentFailure,
  deleteDepartmentSuccess,
  loadDepartments,
  updateDepartment,
  updateDepartmentSuccess
} from '../../../store/department/department.actions'
import { Department } from '../../../store/department/department.state'
import { selectDepartments, selectLoading } from '../../../store/department/department.reducer'
import { selectTotalCount } from '../../../store/department/department.selectors'
import { selectUser } from '../../../store/auth/auth.selectors'
import { AuthState } from '../../../store/auth/auth.state'
// import { eps } from '../../services/url.service'
import { NgSelectModule } from '@ng-select/ng-select'
import { Actions, ofType } from '@ngrx/effects'
import { UtilsService } from '../../../services/utils.service'
// import { noSpacesValidator } from '../../validators/no-spaces.validators'
import { noLeadingTrailingSpaces } from '../../../validators/no-leadingTraillingSpace.validators'
import { HasPermissionDirective } from '../../../directives/has-permission.directive'
// import { TrimInputDirective } from '../../directives/trim-input.directive'
import { SharedModule } from '../../../shared/shared.module'

@Component({
  selector: 'app-department',
  imports: [
    ReactiveFormsModule,
    NgIf,
    CommonModule,
    RouterModule,
    CapitalizePipe,
    BlockUnblockComponent,
    FormsModule,
    DeleteComponent,
    PaginationComponent,
    NgSelectModule,
    // BrowserAnimationsModule
    HasPermissionDirective,
    SharedModule
  ],
  templateUrl: './department.component.html',
  styleUrl: './department.component.scss'
})
export class DepartmentComponent implements OnInit {
  private store = inject(Store) // Inject store using `inject()`
  @ViewChild('addBusinessModals') addBusinessModals!: TemplateRef<any>
  private modalRef!: NgbModalRef
  currentPage = 1
  pageLimit = 10
  loading$: Observable<boolean> = this.store.select(state => state.department.loading)
  error$: Observable<string | null> = this.store.select(state => state.department.error)
  pageLastRecord = 0
  form!: FormGroup
  businesses$: Observable<Business[]> = this.store.select(selectAllBusinesses)
  departments$: Observable<Department[]> = this.store.select(selectDepartments)
  totalCount$!: Observable<number>
  totalCount = 0
  search = ''
  blockDepartmentId = ''
  showModal = false
  showDeleteModal = false
  isBlocking!: boolean // Assuming this will be set before opening the modal
  user: AuthState['user'] | null = null
  businessIdss!: string[]
  businessId!: string | null
  title = ''
  status = false
  isLoading$ = this.store.pipe(select(selectLoading))
  private destroyed$ = new Subject<void>()

  constructor(
    private modalService: NgbModal,
    private fb: FormBuilder,
    private actions$: Actions,
    private utilsService: UtilsService
  ) {
    // this.store.dispatch(loadBusinesses({ params: null }))
    this.departments$ = this.store.select(selectDepartments)
    this.totalCount$ = this.store.select(selectTotalCount).pipe(
      map(totalCount => totalCount || 0) // Default to 0 if totalCount is null
    )

    // Subscribe to totalCount$ safely
    this.totalCount$.subscribe(count => {
      this.totalCount = count // Define this.totalCount in your class
    })

    this.store
      .select(selectUser)
      .pipe(take(1))
      .subscribe(user => {
        this.user = user

        //this.selectedBusiness = user?.business?.id

        // this.businessList = user?.businesses
        //if (this.user?.role.name === 'Business Owner') {
        //  this.selectedBusinessOwner = this.user.businessOwnerId
        //}
        // this.getAttendancePolicyList()
        const businessIds = user?.businessIds // your array of business IDs
        // const paramString = `ids=${businessIds?.join(',') ?? ''}`;
        if (businessIds && businessIds.length) {
          this.businessIdss = businessIds
          const paramString = `businessIds=${businessIds.join(',')}&disabled=false`
          this.store.dispatch(loadBusinesses({ params: paramString }))
        } else {
          this.loadAllBusinesses() // Load all businesses initially
          // this.loadAllBusinessOwners()
        }
      })
  }

  ngOnInit() {
    this.status = false
    this.loadDepartments()

    this.totalCount$.subscribe(count => {
      this.totalCount = count
      this.updatePageLastRecord()
    })

    this.form = this.fb.group({
      departmentName: [
        '',
        [Validators.required, Validators.minLength(2), noLeadingTrailingSpaces()]
      ], // Example validators
      businessId: ['', Validators.required]
      // businessOwnerId: ['']
    })

    this.businesses$.subscribe(businesses => {
      if (businesses.length === 1) {
        this.form.get('businessId')?.setValue(businesses[0].id)
        // this.selectedBusinessId = businesses[0].id
        this.businessId = businesses[0].id
        // this.onBusinessChange(businesses[0].id)
        this.loadDepartments()
      }
    })

    this.actions$
      .pipe(
        ofType(
          createDepartmentSuccess,
          updateDepartmentSuccess,
          deleteDepartmentFailure,
          deleteDepartmentSuccess
        ),
        takeUntil(this.destroyed$) // don't forget to clean up
      )
      .subscribe(() => {
        this.loadAllBusinesses()
        this.loadDepartments()
        this.form.reset()
        this.modalService.dismissAll() // or modalRef.close() if you're using one
        this.blockDepartmentId = ''
      })

    this.actions$
      .pipe(
        ofType(blockDepartmentFailure, blockDepartmentSuccess),
        takeUntil(this.destroyed$) // don't forget to clean up
      )
      .subscribe(() => {
        this.status = !this.status
        this.loadDepartments()
        this.form.reset()
        this.modalService.dismissAll() // or modalRef.close() if you're using one
        this.blockDepartmentId = ''
      })
  }

  loadAllBusinesses() {
    const params: any = {}
    if (this.businessIdss?.length) {
      params.businessIds = this.businessIdss.join(',')
    }
    params.disabled = false
    this.store.dispatch(
      loadBusinesses({
        params: new URLSearchParams(params).toString()
      })
    )
  }

  loadDepartments() {
    // this.currentPage = 1
    this.updatePageLastRecord()
    const params: any = {}
    if (this.businessIdss?.length) {
      params.businessIds = this.businessIdss.join(',')
    }

    if (this.search) {
      params.departmentName = this.search
    }

    if (this.status) {
      params.disabled = this.status
    }

    if (this.businessId) {
      params.businessId = this.businessId
    }

    this.store.dispatch(
      loadDepartments({
        params: new URLSearchParams(params).toString(),
        page: this.currentPage,
        limit: this.pageLimit
      })
    )
  }

  onSearch() {
    this.currentPage = 1 // Reset to the first page on search
    const params = this.search ? `departmentName=${encodeURIComponent(this.search)}` : null

    this.store.dispatch(loadDepartments({ params, page: this.currentPage, limit: this.pageLimit })) // Dispatch action with filter
  }

  onPageChange(newPage: number): void {
    if (newPage > 0) {
      this.currentPage = newPage
      this.loadDepartments() // Reload businesses for the new page
      this.updatePageLastRecord()
    }
  }

  private updatePageLastRecord() {
    this.pageLastRecord = Math.min(this.currentPage * this.pageLimit, this.totalCount)
  }

  onPageLimitChange() {
    this.currentPage = 1 // Reset to first page when limit changes
    this.loadDepartments() // Load employees with new page limit
  }

  onBusinessSelect(businessId: string) {
    if (businessId) {
      // Call a method to fetch data based on the selected owner
      const params = `businessId=${encodeURIComponent(businessId)}`

      this.store.dispatch(
        loadDepartments({ params, page: this.currentPage, limit: this.pageLimit })
      ) // Dispatch action with filter
    } else {
      const params = ''
      this.store.dispatch(
        loadDepartments({ params, page: this.currentPage, limit: this.pageLimit })
      ) // Dispatch action with filter
      // Handle the case where no owner is selected
      // console.log('No business selected')
    }
  }

  onStatusSelect(status: string) {
    if (status) {
      // Call a method to fetch data based on the selected owner
      const isDisabled = status === 'true' ? true : status === 'false' ? false : null

      const params = isDisabled !== null ? `disabled=${isDisabled}` : ''

      this.store.dispatch(
        loadDepartments({ params, page: this.currentPage, limit: this.pageLimit })
      ) // Dispatch action with filter
    } else {
      const params = ''
      this.store.dispatch(
        loadDepartments({ params, page: this.currentPage, limit: this.pageLimit })
      ) // Dispatch action with filter
      // Handle the case where no owner is selected
      console.log('No status selected')
    }
  }

  openModal(isBlocking: boolean, departmentId: string) {
    const modalRef = this.modalService.open(BlockUnblockComponent)
    modalRef.componentInstance.isBlocking = isBlocking
    modalRef.componentInstance.recordId = departmentId
    // this.blockBusinessId = businessId

    modalRef.result.then(
      result => {
        // Handle the result from the modal
        if (result) {
          // Call the service to update the business status
          this.handleConfirm(result)
        }
      },
      reason => {
        // Handle the dismiss case
        if (reason) {
          this.handleConfirm(reason)
        }
      }
    )
  }

  handleConfirm(data: { id: string; status: boolean }) {
    this.store.dispatch(blockDepartment({ id: data.id, disabled: data.status }))
  }

  openDeleteModal(departmentId: string) {
    const modalRef = this.modalService.open(DeleteComponent)
    modalRef.componentInstance.recordId = departmentId
    modalRef.result.then(
      result => {
        // Handle the result from the modal
        if (result) {
          // Call the service to update the business status
          this.handleDeleteConfirm(result)
        }
      },
      reason => {
        // Handle the dismiss case
        if (reason) {
          this.handleDeleteConfirm(reason)
        }
      }
    )
  }

  handleDeleteConfirm(data: { id: string }) {
    this.store.dispatch(deleteDepartment({ id: data.id }))
  }

  onSubmit() {
    if (this.form.valid && !this.blockDepartmentId) {
      this.store.dispatch(
        createDepartment({
          departmentName: this.form.get('departmentName')?.value,
          businessId: this.form.get('businessId')?.value
        })
      )
    } else if (this.form.valid && this.blockDepartmentId) {
      this.store.dispatch(
        updateDepartment({
          id: this.blockDepartmentId,
          departmentName: this.form.get('departmentName')?.value,
          businessId: this.form.get('businessId')?.value
        })
      )
    }
  }

  openBusinessModal(content: TemplateRef<any>, options: any = {}) {
    this.modalRef = this.modalService.open(content, {
      scrollable: false,
      centered: true,
      size: 'md',
      ...options
    })
    this.title = 'Add'
  }

  onClose() {
    // if (this.form) {
    //   this.form.reset(); // Reset the form if it is defined
    // }
    // this.modalService.dismissAll();
    // this.currentStep = 1;
    this.blockDepartmentId = ''
    try {
      if (this.form) {
        this.form.reset()
      }
      if (this.modalRef) {
        this.modalRef.close()
      }
      this.modalService.dismissAll()
    } catch (error) {
      console.error('Error closing modal:', error)
    }
  }
  // getErrorMessage(controlName: string): string | null {
  //   const control = this.form.get(controlName)
  //   // console.log('control name', control)
  //   if (control && control.touched && control.invalid) {
  //     if (control.hasError('required')) {
  //       return 'This field is required'
  //     }
  //     if (control?.hasError('minLength')) {
  //       const minLength = control.getError('minLength').requiredLength
  //       return `Minimum length is ${minLength} characters`
  //     }
  //   }
  //   return null // No error
  // }

  openEditDepartment(content: any, department: any) {
    // this.Editdepartment = department
    this.title = 'Edit'
    this.blockDepartmentId = department.id

    this.form.get('departmentName')?.setValue(department.departmentName)
    this.form.get('businessId')?.setValue(department.businessId)
    this.modalService.open(content, { scrollable: false, centered: true, size: 'md' })
  }

  getDashboardRoute(): string {
    // return this.user?.role === 'Admin' ? eps.dashboardPath : eps.employeeDashboardPath
    // const isAdmin = this.user?.userRoles?.some(role => role === 'Admin')
    // return isAdmin ? eps.dashboardPath : eps.employeeDashboardPath

    return this.utilsService.getDashboardRoute()
  }

  getErrorMessage(controlName: string): string | null {
    const control = this.form.get(controlName)
    if (control && control.touched && control.errors) {
      for (const errorName in control.errors) {
        if (this.errorMessages[errorName]) {
          return this.errorMessages[errorName](control.errors[errorName])
        }
      }
    }
    return null
  }

  private errorMessages: { [key: string]: (error: any) => string } = {
    required: () => 'This field is required',
    minlength: error => `Minimum length is ${error.requiredLength} characters`,
    maxlength: error => `Maximum length is ${error.requiredLength} characters`
    // Add more error types here as needed
  }

  setCurrentPage() {
    this.currentPage = 1
  }

  customSearchFn = (term: string, item: any) => {
    const fullName = `${item.businessName}`.toLowerCase()
    return fullName.includes(term.toLowerCase())
  }
}
